import { useState } from "react";
import { IWorkingHour } from "../../models/IWorkingHour";
import { postWorkingHour, putWorkingHour } from "../../services/restaurant";
import { IScheduleOverride } from "../models/IAvailability";
import { deleteWorkingHour } from "../services/availabilityService";
import {
  DAYS, MONTHS, apiErrorMessage, crossesMidnight, dateForDay, hourForDay, isoDay, overrideOnDate, toHHmm, toTimeSpan,
} from "../utils/workingHoursUtils";
import TimeSelect from "./TimeSelect";
import BulkHoursModal from "./BulkHoursModal";

interface Props {
  restaurantId: string;
  hours: IWorkingHour[];
  overrides: IScheduleOverride[];
  loading: boolean;
  onChanged: () => void;
}

type Popup = { mode: "add"; dayOfWeek: number } | { mode: "edit"; hour: IWorkingHour };

export default function WeeklySchedule({ restaurantId, hours, overrides, loading, onChanged }: Props) {
  const [popup, setPopup] = useState<Popup | null>(null);
  const [form, setForm] = useState({ openTime: "09:00", closeTime: "22:00", isEnabled: true });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  const todayIso = isoDay(new Date());

  const openPopup = (day: number) => {
    const h = hourForDay(hours, day);
    setError("");
    setConfirmDelete(false);
    if (h) {
      setPopup({ mode: "edit", hour: h });
      setForm({ openTime: toHHmm(h.openTime), closeTime: toHHmm(h.closeTime), isEnabled: h.isEnabled });
    } else {
      setPopup({ mode: "add", dayOfWeek: day });
      setForm({ openTime: "09:00", closeTime: "22:00", isEnabled: true });
    }
  };

  const handleSave = async () => {
    if (!popup) return;
    if (form.openTime === form.closeTime) { setError("Açılış ve kapanış saati aynı olamaz."); return; }
    setSaving(true);
    setError("");
    try {
      if (popup.mode === "add") {
        await postWorkingHour({
          restourantId: restaurantId,
          dayOfWeek: popup.dayOfWeek,
          openTime: toTimeSpan(form.openTime),
          closeTime: toTimeSpan(form.closeTime),
        });
      } else {
        await putWorkingHour({
          id: popup.hour.id,
          dayOfWeek: popup.hour.dayOfWeek,
          openTime: toTimeSpan(form.openTime),
          closeTime: toTimeSpan(form.closeTime),
          isEnabled: form.isEnabled,
        });
      }
      setPopup(null);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Kayıt başarısız."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (popup?.mode !== "edit") return;
    setSaving(true);
    setError("");
    try {
      await deleteWorkingHour(popup.hour.id);
      setPopup(null);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Silinemedi."));
    } finally {
      setSaving(false);
    }
  };

  const popupDay = popup ? (popup.mode === "add" ? popup.dayOfWeek : popup.hour.dayOfWeek) : 0;
  const overnight = crossesMidnight(form.openTime, form.closeTime);
  const enabledCount = hours.filter((h) => h.isEnabled).length;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <p style={{ color: "#6b7280", fontSize: ".84rem", margin: 0 }}>
          Bir güne tıklayarak saat ekleyin, düzenleyin ya da silin.
        </p>
        <button className="wh-btn wh-btn--primary" onClick={() => setShowBulk(true)} disabled={loading}>
          <i className="bi bi-calendar-week"></i>Haftayı toplu düzenle
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <div className="spinner-border text-warning" role="status" />
        </div>
      ) : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px", padding: "4px 4px 8px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(110px, 1fr))", gap: 12, minWidth: 760 }}>
            {DAYS.map((day) => {
              const h = hourForDay(hours, day.value);
              const weekend = day.value >= 6;
              const today = day.value === todayIso;
              const date = dateForDay(day.value);
              const ov = overrideOnDate(overrides, date);
              const night = h ? crossesMidnight(h.openTime, h.closeTime) : false;
              return (
                <div
                  key={day.value}
                  className="wh-day-card"
                  onClick={() => openPopup(day.value)}
                  style={{
                    boxShadow: today ? "0 4px 20px rgba(99,102,241,0.22)" : h ? "0 4px 16px rgba(0,0,0,0.08)" : "0 2px 8px rgba(0,0,0,0.05)",
                    background: h ? "#fff" : "#f9fafb",
                    border: today ? "2px solid #6366f1" : h ? `2px solid ${weekend ? "#fde68a" : "#d1fae5"}` : "2px solid #f3f4f6",
                  }}
                >
                  <div style={{
                    padding: "10px 8px 8px", textAlign: "center",
                    background: today
                      ? "linear-gradient(135deg, #eef2ff, #c7d2fe)"
                      : h ? (weekend ? "linear-gradient(135deg, #fef3c7, #fde68a)" : "linear-gradient(135deg, #d1fae5, #a7f3d0)") : "#f3f4f6",
                  }}>
                    {today && <div style={{ fontSize: "0.6rem", fontWeight: 800, color: "#4f46e5", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 2 }}>Bugün</div>}
                    <div style={{ fontSize: "0.68rem", fontWeight: 700, color: today ? "#4f46e5" : h ? (weekend ? "#92400e" : "#065f46") : "#9ca3af", letterSpacing: "0.5px", textTransform: "uppercase" }}>{day.short}</div>
                    <div style={{ fontSize: "1.1rem", fontWeight: 800, color: today ? "#3730a3" : h ? (weekend ? "#78350f" : "#064e3b") : "#d1d5db", marginTop: 1, lineHeight: 1 }}>{date.getDate()}</div>
                    <div style={{ fontSize: "0.62rem", fontWeight: 600, color: today ? "#6366f1" : h ? (weekend ? "#92400e" : "#065f46") : "#9ca3af", marginTop: 1 }}>{MONTHS[date.getMonth()]}</div>
                  </div>

                  <div style={{ padding: "12px 8px", textAlign: "center" }}>
                    {h ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: h.isEnabled ? "#10b981" : "#f87171", flexShrink: 0 }} />
                          <span style={{ fontSize: "0.68rem", fontWeight: 600, color: h.isEnabled ? "#059669" : "#dc2626" }}>{h.isEnabled ? "Açık" : "Kapalı"}</span>
                        </div>
                        <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "5px 4px", marginBottom: 5, opacity: h.isEnabled ? 1 : 0.5 }}>
                          <div style={{ fontSize: "0.6rem", color: "#6b7280", marginBottom: 1 }}>Açılış</div>
                          <div style={{ fontSize: "0.88rem", fontWeight: 800, color: "#065f46" }}>{toHHmm(h.openTime)}</div>
                        </div>
                        <div style={{ background: "#fef2f2", borderRadius: 8, padding: "5px 4px", opacity: h.isEnabled ? 1 : 0.5 }}>
                          <div style={{ fontSize: "0.6rem", color: "#6b7280", marginBottom: 1 }}>Kapanış</div>
                          <div style={{ fontSize: "0.88rem", fontWeight: 800, color: "#991b1b" }}>
                            {toHHmm(h.closeTime)}
                            {night && <span title="Ertesi gün" style={{ fontSize: "0.58rem", fontWeight: 800, color: "#6366f1", marginLeft: 3, verticalAlign: "super" }}>+1</span>}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ padding: "16px 0", color: "#d1d5db" }}>
                        <i className="bi bi-plus-circle" style={{ fontSize: "1.4rem", display: "block", marginBottom: 4 }}></i>
                        <span style={{ fontSize: "0.68rem", fontWeight: 600 }}>Ekle</span>
                      </div>
                    )}

                    {ov && (
                      <div title={ov.reason ?? undefined} style={{
                        marginTop: 8, fontSize: "0.62rem", fontWeight: 800, borderRadius: 6, padding: "3px 4px",
                        background: ov.isClosed ? "#fee2e2" : "#dcfce7", color: ov.isClosed ? "#b91c1c" : "#15803d",
                      }}>
                        <i className={`bi ${ov.isClosed ? "bi-calendar-x" : "bi-calendar-check"} me-1`}></i>
                        {ov.isClosed ? "Özel kapanış" : "Özel açılış"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (
        <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 12, background: "#f8faff", border: "1.5px solid #e5e7eb", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>
            <i className="bi bi-calendar-check me-1 text-success"></i><b>{enabledCount}</b> gün açık
          </span>
          <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>
            <i className="bi bi-clock me-1 text-warning"></i>{hours.length} / 7 gün tanımlı
          </span>
          {hours.some((h) => h.isEnabled && crossesMidnight(h.openTime, h.closeTime)) && (
            <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>
              <span style={{ fontWeight: 800, color: "#6366f1" }}>+1</span> kapanışı ertesi güne taşan vardiya
            </span>
          )}
        </div>
      )}

      {/* Gün penceresi */}
      {popup && (
        <div className="wh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) setPopup(null); }}>
          <div className="wh-modal" style={{ width: "min(96vw, 400px)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #fef3c7, #fde68a)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-clock-fill" style={{ color: "#d97706", fontSize: "1rem" }}></i>
                </div>
                {popup.mode === "edit" ? "Saati Düzenle" : "Saat Ekle"}
              </div>
              <button onClick={() => setPopup(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.2rem" }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 12, background: "#f3f4f6" }}>
                <i className="bi bi-calendar3" style={{ color: "#6b7280" }}></i>
                <span style={{ fontWeight: 700, color: "#111827", fontSize: "0.9rem" }}>{DAYS[popupDay - 1]?.label}</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <span className="wh-label"><i className="bi bi-sunrise-fill me-1" style={{ color: "#f59e0b" }}></i>Açılış</span>
                  <TimeSelect value={form.openTime} onChange={(v) => setForm((p) => ({ ...p, openTime: v }))} />
                </div>
                <div>
                  <span className="wh-label"><i className="bi bi-moon-fill me-1" style={{ color: "#6366f1" }}></i>Kapanış</span>
                  <TimeSelect value={form.closeTime} onChange={(v) => setForm((p) => ({ ...p, closeTime: v }))} />
                </div>
              </div>

              {overnight && (
                <div className="wh-note wh-note--info">
                  <i className="bi bi-moon-stars-fill"></i>
                  <span>Kapanış açılıştan önce: vardiya <b>ertesi gün {form.closeTime}</b>'a kadar sürer.</span>
                </div>
              )}

              {popup.mode === "edit" && (
                <div
                  onClick={() => setForm((p) => ({ ...p, isEnabled: !p.isEnabled }))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 12, border: "1.5px solid #e5e7eb", cursor: "pointer", userSelect: "none" }}
                >
                  <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 10 }}>
                    <span className={`wh-switch ${form.isEnabled ? "wh-switch--on" : "wh-switch--off"}`} />
                    Restoran bu gün
                  </span>
                  <span style={{ fontSize: "0.78rem", fontWeight: 700, color: form.isEnabled ? "#059669" : "#6b7280" }}>{form.isEnabled ? "Açık" : "Kapalı"}</span>
                </div>
              )}

              {error && <div className="wh-note wh-note--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "space-between", flexWrap: "wrap" }}>
              <div>
                {popup.mode === "edit" && (
                  confirmDelete ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="wh-btn wh-btn--danger" onClick={handleDelete} disabled={saving}><i className="bi bi-trash3"></i>Evet, sil</button>
                      <button className="wh-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>Hayır</button>
                    </div>
                  ) : (
                    <button className="wh-btn wh-btn--ghost-danger" onClick={() => setConfirmDelete(true)} disabled={saving} title="Bu günün kaydını sil">
                      <i className="bi bi-trash3"></i>Sil
                    </button>
                  )
                )}
              </div>
              {!confirmDelete && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="wh-btn" onClick={() => setPopup(null)} disabled={saving}>İptal</button>
                  <button className="wh-btn wh-btn--primary" onClick={handleSave} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor…</> : <><i className="bi bi-check-lg"></i>Kaydet</>}
                  </button>
                </div>
              )}
            </div>
            {confirmDelete && (
              <div className="wh-hint" style={{ marginTop: 10 }}>
                Silinen gün "tanımsız" olur ve restoran o gün kapalı sayılır. Sadece bir günlüğüne kapatmak için "Restoran bu gün: Kapalı" yeterli.
              </div>
            )}
          </div>
        </div>
      )}

      {showBulk && <BulkHoursModal restaurantId={restaurantId} hours={hours} onClose={() => setShowBulk(false)} onSaved={onChanged} />}
    </>
  );
}
